import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gamepad2,
  Plus,
  Play,
  BookOpen,
  Users,
  Trophy,
  Clock,
  Zap,
  Target,
  Brain,
  Sparkles,
  ChevronRight,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

// Subject colors
const subjectColors = {
  matematica: 'bg-blue-500',
  portugues: 'bg-red-500',
  ciencias: 'bg-green-500',
  historia: 'bg-amber-500',
  geografia: 'bg-emerald-500',
  ingles: 'bg-purple-500',
  artes: 'bg-pink-500',
  educacao_fisica: 'bg-orange-500',
  filosofia: 'bg-indigo-500',
  sociologia: 'bg-teal-500'
};

const subjectNames = {
  matematica: 'Matemática',
  portugues: 'Português',
  ciencias: 'Ciências',
  historia: 'História',
  geografia: 'Geografia',
  ingles: 'Inglês',
  artes: 'Artes',
  educacao_fisica: 'Ed. Física',
  filosofia: 'Filosofia',
  sociologia: 'Sociologia'
};

export default function QuizLive() {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [sessionMode, setSessionMode] = useState('individual');
  const [stats, setStats] = useState({ quiz_count: 0, total_sessions: 0, total_players: 0 });

  useEffect(() => {
    loadQuizzes();
    loadStats();
  }, []);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/quiz-live/quizzes');
      setQuizzes(response.data);
    } catch (error) {
      console.error('Error loading quizzes:', error);
      toast.error('Erro ao carregar quizzes');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // TODO: Get teacher_id from auth context
      const response = await api.get('/quiz-live/stats/teacher/current');
      setStats(response.data);
    } catch (error) {
      // Stats are optional, don't show error
    }
  };

  const handleStartSession = async () => {
    if (!selectedQuiz) return;

    try {
      const response = await api.post('/quiz-live/sessions', {
        quiz_id: selectedQuiz.id,
        mode: sessionMode,
        max_players: 50
      });

      toast.success('Sessão criada!');
      setShowStartDialog(false);
      navigate(`/quiz-live/lobby/${response.data.code}`);
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Erro ao criar sessão');
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!confirm('Tem certeza que deseja excluir este quiz?')) return;

    try {
      await api.delete(`/quiz-live/quizzes/${quizId}`);
      toast.success('Quiz excluído');
      loadQuizzes();
    } catch (error) {
      toast.error('Erro ao excluir quiz');
    }
  };

  const handleDuplicateQuiz = async (quiz) => {
    try {
      const newQuiz = {
        ...quiz,
        title: `${quiz.title} (Cópia)`,
        id: undefined
      };
      await api.post('/quiz-live/quizzes', newQuiz);
      toast.success('Quiz duplicado');
      loadQuizzes();
    } catch (error) {
      toast.error('Erro ao duplicar quiz');
    }
  };

  const filteredQuizzes = quizzes.filter(quiz => {
    const matchesSearch = quiz.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = filterSubject === 'all' || quiz.subject === filterSubject;
    return matchesSearch && matchesSubject;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-green-500 p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur">
              <Gamepad2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">DNA Quiz Live</h1>
              <p className="text-white/80">Aprendizado gamificado em tempo real</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <BookOpen className="w-6 h-6 text-white/80 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.quiz_count}</p>
              <p className="text-sm text-white/70">Quizzes</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <Play className="w-6 h-6 text-white/80 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.total_sessions}</p>
              <p className="text-sm text-white/70">Sessões</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <Users className="w-6 h-6 text-white/80 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.total_players}</p>
              <p className="text-sm text-white/70">Jogadores</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card 
            className="bg-gradient-to-br from-purple-600 to-purple-800 border-0 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => navigate('/quiz-live/criar')}
            data-testid="create-quiz-card"
          >
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Criar Quiz</h3>
              <p className="text-white/70 text-sm">Monte suas questões</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-green-500 to-green-700 border-0 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => navigate('/quiz-live/ai')}
            data-testid="ai-quiz-card"
          >
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Quiz com IA</h3>
              <p className="text-white/70 text-sm">Gere com inteligência</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-amber-500 to-orange-600 border-0 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => navigate('/quiz-live/biblioteca')}
            data-testid="library-card"
          >
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Biblioteca</h3>
              <p className="text-white/70 text-sm">Explore quizzes</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-blue-500 to-blue-700 border-0 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => navigate('/quiz-live/relatorios')}
            data-testid="reports-card"
          >
            <CardContent className="p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Relatórios</h3>
              <p className="text-white/70 text-sm">Analise resultados</p>
            </CardContent>
          </Card>
        </div>

        {/* My Quizzes Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-2xl font-bold text-white">Meus Quizzes</h2>
            <div className="flex gap-3">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar quiz..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
              <Select value={filterSubject} onValueChange={setFilterSubject}>
                <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Disciplina" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(subjectNames).map(([key, name]) => (
                    <SelectItem key={key} value={key}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <Card key={i} className="bg-white/5 border-white/10 animate-pulse">
                  <CardContent className="p-6 h-48"></CardContent>
                </Card>
              ))}
            </div>
          ) : filteredQuizzes.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-12 text-center">
                <BookOpen className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Nenhum quiz encontrado</h3>
                <p className="text-gray-400 mb-4">Crie seu primeiro quiz para começar</p>
                <Button onClick={() => navigate('/quiz-live/criar')} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Quiz
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredQuizzes.map(quiz => (
                <Card 
                  key={quiz.id} 
                  className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors group"
                  data-testid={`quiz-card-${quiz.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-3 h-3 rounded-full ${subjectColors[quiz.subject] || 'bg-gray-500'}`} />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/quiz-live/editar/${quiz.id}`)}>
                            <Edit className="w-4 h-4 mr-2" /> Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicateQuiz(quiz)}>
                            <Copy className="w-4 h-4 mr-2" /> Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteQuiz(quiz.id)}
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                      {quiz.title}
                    </h3>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge variant="outline" className="text-xs border-white/20 text-gray-300">
                        {subjectNames[quiz.subject] || quiz.subject}
                      </Badge>
                      <Badge variant="outline" className="text-xs border-white/20 text-gray-300">
                        {quiz.question_count} questões
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                      <span className="flex items-center gap-1">
                        <Play className="w-4 h-4" />
                        {quiz.play_count || 0} jogos
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        ~{Math.ceil((quiz.question_count || 5) * 0.5)}min
                      </span>
                    </div>

                    <Button 
                      className="w-full bg-gradient-to-r from-purple-600 to-green-500 hover:from-purple-700 hover:to-green-600"
                      onClick={() => {
                        setSelectedQuiz(quiz);
                        setShowStartDialog(true);
                      }}
                      data-testid={`start-quiz-${quiz.id}`}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Iniciar Sessão
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Start Session Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle>Iniciar Sessão</DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure as opções da sessão de quiz
            </DialogDescription>
          </DialogHeader>

          {selectedQuiz && (
            <div className="space-y-6 py-4">
              <div className="p-4 bg-white/5 rounded-xl">
                <h4 className="font-medium mb-1">{selectedQuiz.title}</h4>
                <p className="text-sm text-gray-400">
                  {selectedQuiz.question_count} questões • {subjectNames[selectedQuiz.subject]}
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-medium">Modo de Jogo</label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    onClick={() => setSessionMode('individual')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      sessionMode === 'individual'
                        ? 'border-purple-500 bg-purple-500/20'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <Users className="w-6 h-6 mb-2 text-purple-400" />
                    <h5 className="font-medium">Individual</h5>
                    <p className="text-xs text-gray-400">Cada um por si</p>
                  </div>
                  <div
                    onClick={() => setSessionMode('teams')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      sessionMode === 'teams'
                        ? 'border-green-500 bg-green-500/20'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <Target className="w-6 h-6 mb-2 text-green-400" />
                    <h5 className="font-medium">Equipes</h5>
                    <p className="text-xs text-gray-400">Colaboração</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleStartSession}
              className="bg-gradient-to-r from-purple-600 to-green-500"
              data-testid="confirm-start-session"
            >
              <Play className="w-4 h-4 mr-2" />
              Criar Sessão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
